import React, { Suspense, useLayoutEffect, useState, useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, X, BookOpen } from 'lucide-react';
import { SolubilityScene3D } from './lab/SolubilityScene3D';
import { SolubilitySidebar } from './panels/SolubilitySidebar';
import { CameraController } from './lab/CameraController';
import SolubilityToolbar from './lab/SolubilityToolbar';
import LoaderOverlay from './lab/LoaderOverlay';
import { useSolubilityStore } from './store/useSolubilityStore';
import { ThemeManager } from '../titration/lib/ThemeManager';
import { useCurrentUser } from '../../AuthComponents';
import {
  useExperiment,
  useStudentProgress,
  usePreLabQuestions,
  useDiscussionQuestions,
  useSubmitPreLabAssessment,
  useTeacherBypass,
  useSaveLabData,
} from '../../../utils/convexHooks';
import { Id } from '../../../convex/_generated/dataModel';
import PreLabAssessment from '../../PreLabAssessment';

function WebGLStateTracker({ onReady }: { onReady: () => void }) {
  const frameCount = useRef(0);
  useFrame(() => {
    frameCount.current += 1;
    if (frameCount.current === 2) onReady();
  });
  return null;
}

function TutorialOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative max-w-xl w-[92vw] bg-card text-foreground rounded-2xl shadow-2xl border border-border p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-muted/70 text-muted-foreground hover:text-foreground transition"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
              How this experiment works
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Effect of temperature on solubility of salt in water</p>
          </div>
        </div>

        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="rounded-lg bg-muted/50 border border-border p-3">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mb-1">Aim</p>
            <p className="text-foreground">Study how temperature affects how much salt can dissolve in 50 mL of water.</p>
          </div>

          <p className="text-xs font-bold uppercase tracking-widest text-blue-500 mt-4">Procedure for each trial</p>
          <ol className="list-decimal list-inside space-y-1.5 ml-2 text-foreground">
            <li>Pour 50 mL of water into the beaker.</li>
            <li>Weigh 40 g of salt on the electronic scale (click 8× at +5 g).</li>
            <li>Transfer the salt into the beaker.</li>
            <li>For 30 °C and 80 °C: light the Bunsen burner and heat to the target.</li>
            <li>Stir for 2 minutes (use the speed-up button to skip ahead).</li>
            <li>Click <em>Record Observation</em> to save the trial.</li>
          </ol>
          <p>Repeat for all three trials (10 °C, 30 °C, 80 °C). Compare results in Data and Chart.</p>

          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trial 1</p>
              <p className="text-lg font-bold text-blue-500">10 °C</p>
              <p className="text-[11px] text-muted-foreground">cold preset</p>
            </div>
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trial 2</p>
              <p className="text-lg font-bold text-orange-500">30 °C</p>
              <p className="text-[11px] text-muted-foreground">heat &amp; stir</p>
            </div>
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trial 3</p>
              <p className="text-lg font-bold text-red-500">80 °C</p>
              <p className="text-[11px] text-muted-foreground">heat &amp; stir</p>
            </div>
          </div>

          <div className="pt-2 border-t border-border text-xs">
            <p>
              <strong className="text-foreground">Tip:</strong> The fast-forward button (1× → 2× → 5× → 10×) speeds up heating, stirring and dissolution all at once. <strong className="text-foreground">Auto-Demo</strong> runs the entire experiment for you.
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="mt-5 w-full py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium transition shadow-md shadow-blue-500/20"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

interface BaseProps {
  experimentConfig: any;
  progress: any;
  discussionQuestions: any;
}

function SolubilityExperimentBase({ experimentConfig }: BaseProps) {
  const resetAll = useSolubilityStore(s => s.resetAll);
  const results = useSolubilityStore(s => s.results);
  const showTutorial = useSolubilityStore(s => s.showTutorial);
  const setShowTutorial = useSolubilityStore(s => s.setShowTutorial);
  const [webGLReady, setWebGLReady] = useState(false);
  const controlsRef = useRef<any>(null);

  const { user } = useCurrentUser();
  const saveLabData = useSaveLabData();

  useLayoutEffect(() => {
    ThemeManager.getInstance().setMode(ThemeManager.getInstance().getMode());
  }, []);

  useEffect(() => {
    resetAll();
  }, [resetAll]);

  const savedRef = useRef(false);
  useEffect(() => {
    if (results.length === 3 && !savedRef.current && user && experimentConfig?._id) {
      savedRef.current = true;
      saveLabData({
        studentId: user._id,
        experimentConfigId: experimentConfig._id,
        dataPoints: results,
      }).catch((e: any) => console.error('saveLabData failed:', e));
    }
  }, [results, user, experimentConfig, saveLabData]);

  const title = experimentConfig?.title || 'Salt Solubility Experiment';
  const experimentMode = experimentConfig?.experimentMode || 'practice';

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden bg-background relative text-foreground">
      <SolubilityToolbar title={title} experimentMode={experimentMode} />

      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden relative w-full h-full bg-background">
        <div className="flex-[2] lg:flex-[3] relative w-full h-full min-h-[50vh] lg:min-h-0 bg-background/50">
          <div className="absolute inset-0">
            <LoaderOverlay isWebGLReady={webGLReady} />
            <Canvas shadows dpr={[1, 2]} gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }} className="w-full h-full">
              <WebGLStateTracker onReady={() => setWebGLReady(true)} />
              <PerspectiveCamera makeDefault position={[0, 1.5, 4]} fov={45} />
              <OrbitControls ref={controlsRef} enablePan panSpeed={0.5} minDistance={0.5} maxDistance={15} target={[0, 1, 0]} />
              <CameraController controlsRef={controlsRef} />
              <ambientLight intensity={0.55} />
              <directionalLight position={[5, 8, 5]} intensity={0.85} />
              <directionalLight position={[-3, 4, -2]} intensity={0.3} color="#8ab4ff" />
              <Suspense fallback={null}>
                <SolubilityScene3D />
              </Suspense>
            </Canvas>
          </div>
        </div>
        <SolubilitySidebar />
      </div>

      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}
    </div>
  );
}

export default function SolubilityExperimentWrapper() {
  const [searchParams] = useSearchParams();
  const idStr = searchParams.get('id');
  const id = idStr ? (idStr as Id<'experimentConfigs'>) : undefined;

  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const experimentConfig = useExperiment(id);
  const progress = useStudentProgress(user?._id, id);
  const preLabQuestions = usePreLabQuestions(id);
  const discussionQuestions = useDiscussionQuestions(id);
  const submitPreLab = useSubmitPreLabAssessment();
  const teacherBypass = useTeacherBypass();

  if (!id) {
    return <SolubilityExperimentBase experimentConfig={null} progress={null} discussionQuestions={null} />;
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <p className="text-xl">Please sign in to start the experiment.</p>
        <button onClick={() => navigate('/sign-in')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Sign in</button>
      </div>
    );
  }

  if (experimentConfig === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <p className="text-xl text-red-500">Experiment not found</p>
        <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back to Dashboard</button>
      </div>
    );
  }

  if (
    experimentConfig === undefined ||
    progress === undefined ||
    (experimentConfig.preLab.enabled && preLabQuestions === undefined)
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <Loader2 className="w-12 h-12 animate-spin text-cyan-500 mb-4" />
        <p className="text-sm text-muted-foreground tracking-wider uppercase">Preparing the lab…</p>
      </div>
    );
  }

  const needsPreLab =
    experimentConfig.preLab.enabled &&
    progress !== null &&
    (progress.status === 'not-started' || progress.status === 'pre-lab') &&
    (!progress.preLab || !progress.preLab.passed);

  if (needsPreLab && preLabQuestions && preLabQuestions.length > 0) {
    return (
      <div className="min-h-screen bg-background py-8 px-4 text-foreground">
        <button
          onClick={() => navigate('/student/dashboard')}
          className="mb-6 flex flex-row gap-2 items-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-5 h-5" /> Back to Dashboard
        </button>
        <PreLabAssessment
          questions={preLabQuestions.map((q: any) => ({ ...q, id: q._id as string }))}
          passingScore={experimentConfig.preLab.passingScore}
          maxAttempts={experimentConfig.preLab.maxAttempts}
          showAnswersAfterFail={experimentConfig.preLab.showAnswersAfterFail}
          allowTeacherBypass={experimentConfig.allowTeacherBypass}
          onPass={async (answers) => {
            try {
              await submitPreLab({
                experimentConfigId: id,
                studentId: user._id,
                answers: answers.map((a: any) => ({
                  questionId: a.questionId as Id<'preLabQuestions'>,
                  answer: a.answer,
                })),
              });
            } catch (e: any) {
              alert(e.message || 'Failed to save pre-lab progress.');
            }
          }}
          onBypass={async () => {
            try {
              await teacherBypass({ experimentConfigId: id, studentId: user._id });
            } catch (e: any) {
              alert(e.message || 'Invalid password');
            }
          }}
        />
      </div>
    );
  }

  return (
    <SolubilityExperimentBase
      experimentConfig={experimentConfig}
      progress={progress}
      discussionQuestions={discussionQuestions}
    />
  );
}
